import { IconAngleRight, IconSearchAlt, mapper } from '@maia/icons';
import { ActionButton, ActionButtonLink, TableCell } from '@maia/ui';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Insurances } from './queryOptions';
import { DrawerStore, useDrawerStore } from './../../../stores/drawer.store';

type Insurance = Insurances['response'][number];

type TableMeta = {
  openDrawer: DrawerStore['openDrawer'];
};

const columns: ColumnDef<Insurance>[] = [
  {
    accessorKey: 'reference',
    header: 'Polisnummer',
    cell: ({ row }) => (
      <TableCell.Link
        to="/insurances/$guid"
        params={{
          guid: row.original.guid,
        }}
      >
        {row.original.reference}
      </TableCell.Link>
    ),
  },
  {
    accessorKey: 'title',
    header: 'Productnaam',
    cell: ({ row }) => (
      <TableCell.Product
        icon={mapper(row.original.icon)}
        title={row.original.title}
        characteristic={row.original.characteristic}
      />
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TableCell.Tag
        {...row.original.status}
        tooltip={row.original.acceptanceRemark ?? undefined}
      />
    ),
  },
  {
    accessorKey: 'price',
    header: 'Premie',
    cell: ({ row }) => (
      <TableCell.Price price={row.original.price.premiumAfterTax} />
    ),
  },

  {
    id: 'actions',
    cell: ({ table, row }) => (
      <div className="flex gap-x-12 items-center place-content-end w-full">
        <ActionButtonLink
          icon={IconAngleRight}
          size="small"
          to="/insurances/$guid"
          params={{ guid: row.original.guid }}
        />
        <ActionButton
          icon={IconSearchAlt}
          size="small"
          onPress={() =>
            (table.options.meta as TableMeta).openDrawer({
              type: 'insurance',
              data: row.original,
            })
          }
        />
      </div>
    ),
  },
];

export function useInsuranceTable(data?: Insurances) {
  const { openDrawer } = useDrawerStore();

  return useReactTable({
    columns,
    data: data?.response ?? [],
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    pageCount: data?.pagination.totalPages,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    meta: {
      openDrawer,
    },
  });
}
